// Phase 8 (Session ATJ) invoice actions: Send payment link (M-9b), Draft chaser (M-7), Raise credit
// note (M-8) and Generate statement (M-6). Every one previews first (the platform-wide convention)
// and names who would be contacted before anything is drafted or raised (the branded-confirm rule,
// Session AEH). None of these ever sends anything to a customer directly: each only ever raises a
// Xero DRAFT, drafts a Gmail email for a person to review and send, or (payment link) raises a
// Stripe link that reaches nobody until that drafted email is sent.
import { Button } from "../../components/Button";
import { useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { PaymentLinkResult, StatementResult, CreditNoteResult, ChaserResult } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

function gbp(n: number | null | undefined): string {
  if (n == null) return "-";
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function refusalMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 503) return "That connection is not set up yet. Check Settings and try again.";
    if (err.status === 422) return (err.body as { message?: string })?.message ?? "This customer has no billing email on file.";
    if (err.status === 409) return (err.body as { message?: string })?.message ?? "That is not currently possible for this invoice.";
    if (err.status === 502) return (err.body as { message?: string })?.message ?? fallback;
  }
  return fallback;
}

// ---- Send payment link (M-9b) -----------------------------------------------------------------
export function SendPaymentLinkModal({ invoiceId, onClose, onDone }: { invoiceId: string; onClose: () => void; onDone: () => void }) {
  const [preview, setPreview] = useState<PaymentLinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.invoices
      .paymentLink(invoiceId, true)
      .then((p) => live && setPreview(p))
      .catch((err) => live && setError(refusalMessage(err, "Could not check whether this invoice can be paid online.")));
    return () => {
      live = false;
    };
  }, [invoiceId]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.invoices.paymentLink(invoiceId, false);
      onDone();
    } catch (err) {
      setError(refusalMessage(err, "Could not raise the payment link."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Send a payment link" onClose={() => !busy && onClose()}>
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Send a payment link</h2>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {!preview && !error ? (
        <p className="text-sm text-ele-body">Checking...</p>
      ) : preview ? (
        <div>
          <p className="mb-4 text-sm text-ele-body">
            Raises a Stripe payment link for {gbp(preview.amount)} and drafts an email to{" "}
            <strong className="text-ele-strong">{preview.to ?? "(no billing email on file)"}</strong> carrying it, in Gmail
            Drafts, for a person to review and send. Nothing is sent from here.
          </p>
          {preview.alreadyHasLink && (
            <p className="mb-4 text-sm text-ele-body">A payment link already exists for this invoice; this reuses it.</p>
          )}
          <div className={`gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            <Button
              onClick={confirm}
              disabled={busy || !preview.to}
              className={MODAL_ACTION_BTN}
            >
              {busy ? "Raising..." : "Raise the link and draft the email"}
            </Button>
          </div>
        </div>
      ) : null}
    </ModalShell>
  );
}

// ---- Draft an overdue chaser (M-7) -------------------------------------------------------------
export function DraftChaserModal({ invoiceId, onClose, onDone }: { invoiceId: string; onClose: () => void; onDone: () => void }) {
  const [preview, setPreview] = useState<ChaserResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.invoices
      .chaser(invoiceId, true)
      .then((p) => live && setPreview(p))
      .catch((err) => live && setError(refusalMessage(err, "Could not check whether this invoice is chaseable.")));
    return () => {
      live = false;
    };
  }, [invoiceId]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.invoices.chaser(invoiceId, false);
      onDone();
    } catch (err) {
      setError(refusalMessage(err, "Could not draft the chaser."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Draft an overdue chaser" onClose={() => !busy && onClose()}>
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Draft an overdue chaser</h2>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {!preview && !error ? (
        <p className="text-sm text-ele-body">Checking...</p>
      ) : preview ? (
        <div>
          <p className="mb-4 text-sm text-ele-body">{preview.message}</p>
          <div className={`gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              {preview.chaseable ? "Cancel" : "Close"}
            </button>
            {preview.chaseable && (
              <Button
                onClick={confirm}
                disabled={busy}
                className={MODAL_ACTION_BTN}
              >
                {busy ? "Drafting..." : "Draft the chaser"}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </ModalShell>
  );
}

// ---- Raise a credit note (M-8) -----------------------------------------------------------------
export function CreditNoteModal({ invoiceId, onClose, onDone }: { invoiceId: string; onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [amountText, setAmountText] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<CreditNoteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const amount = Math.round(Number(amountText) * 100) / 100;

  async function review() {
    setError(null);
    if (!Number.isFinite(Number(amountText)) || amountText.trim() === "" || amount <= 0) return setError("Enter the credit amount, ex VAT.");
    if (reason.trim().length === 0) return setError("Say why this credit is being raised (internal only, never shown to the customer).");
    setBusy(true);
    try {
      const p = await api.invoices.creditNote(invoiceId, amount, reason.trim(), true);
      setPreview(p);
      setStep("confirm");
    } catch (err) {
      setError(refusalMessage(err, "Could not check this credit note."));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.invoices.creditNote(invoiceId, amount, reason.trim(), false);
      onDone();
    } catch (err) {
      setError(refusalMessage(err, "Xero could not create this credit note."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Raise a credit note" onClose={() => !busy && onClose()} width="max-w-[520px]">
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Raise a credit note</h2>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {step === "form" ? (
        <div className="flex flex-col gap-3">
          <div>
            <label className={labelCls} htmlFor="cn-amount">
              Credit amount, ex VAT
            </label>
            <input
              id="cn-amount"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={amountText}
              onChange={(e) => setAmountText(e.target.value)}
              className={inputCls}
              placeholder="50.00"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="cn-reason">
              Reason (internal only; never printed on the credit note or shown to the customer)
            </label>
            <textarea id="cn-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className={inputCls} />
          </div>
          <div className={`gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={onClose}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            <Button
              onClick={review}
              disabled={busy}
              className={MODAL_ACTION_BTN}
            >
              {busy ? "Checking..." : "Continue"}
            </Button>
          </div>
        </div>
      ) : preview ? (
        <div>
          <p className="mb-4 text-sm text-ele-body">{preview.message}</p>
          <p className="mb-4 text-sm text-ele-body">
            This creates a DRAFT credit note in Xero. It is never authorised or allocated against a future invoice from
            here: that stays a Xero/human decision.
          </p>
          <div className={`gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={() => setStep("form")}
              disabled={busy}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Back
            </button>
            <Button
              onClick={confirm}
              disabled={busy}
              className={MODAL_ACTION_BTN}
            >
              {busy ? "Raising..." : "Raise the draft credit note"}
            </Button>
          </div>
        </div>
      ) : null}
    </ModalShell>
  );
}

// ---- Generate a statement (M-6) ----------------------------------------------------------------
export function GenerateStatementModal({
  customerId,
  customerName,
  onClose,
  onDone,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [preview, setPreview] = useState<StatementResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.invoices
      .statement(customerId, true)
      .then((p) => live && setPreview(p))
      .catch((err) => live && setError(refusalMessage(err, "Could not prepare this statement.")));
    return () => {
      live = false;
    };
  }, [customerId]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.invoices.statement(customerId, false);
      onDone();
    } catch (err) {
      setError(refusalMessage(err, "Could not generate the statement."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Generate an account statement" onClose={() => !busy && onClose()}>
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Generate an account statement</h2>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {!preview && !error ? (
        <p className="text-sm text-ele-body">Checking {customerName}'s outstanding invoices...</p>
      ) : preview ? (
        <div>
          <p className="mb-4 text-sm text-ele-body">
            Total outstanding for <strong className="text-ele-strong">{customerName}</strong>: {gbp(preview.totalOutstanding)}{preview.totalOverdue ? `, of which ${gbp(preview.totalOverdue)} is overdue` : ""}.
          </p>
          <p className="mb-4 text-sm text-ele-body">{preview.message}</p>
          <div className={`gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            <Button
              onClick={confirm}
              disabled={busy || !preview.hasBillingEmail}
              className={MODAL_ACTION_BTN}
            >
              {busy ? "Generating..." : "Generate and draft the email"}
            </Button>
          </div>
        </div>
      ) : null}
    </ModalShell>
  );
}
